import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNoticeList, NoticeListItemDto } from '../../api/deskApi';
import {formatDateTime} from "../../utils/format";

const NoticeListPage = () => {
    const navigate = useNavigate();
    const [notices, setNotices] = useState<NoticeListItemDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 10;

    // Intersection Observer를 위한 ref
    const observerTarget = useRef<HTMLDivElement>(null);

    const fetchNotices = useCallback(async (page: number) => {
        if (loading) return;

        try {
            setLoading(true);
            setError(null);
            const response = await getNoticeList({
                page: page,
                size: pageSize,
            });

            if (response.data?.data) {
                const newNotices = response.data.data.rows;

                // 첫 페이지면 교체, 아니면 추가
                if (page === 1) {
                    setNotices(newNotices);
                } else {
                    setNotices(prev => [...prev, ...newNotices]);
                }

                // 더 이상 데이터가 없으면 hasMore를 false로
                if (newNotices.length < pageSize) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            } else {
                setError('공지사항을 불러오는데 실패했습니다.');
            }
        } catch (err) {
            console.error('Failed to fetch notices:', err);
            setError('공지사항을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [loading]);

    // 초기 로드
    useEffect(() => {
        fetchNotices(1);
    }, []);

    // Intersection Observer 설정
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                // 타겟이 화면에 보이고, 더 불러올 데이터가 있고, 로딩 중이 아닐 때
                if (entries[0].isIntersecting && hasMore && !loading) {
                    setCurrentPage(prev => {
                        const nextPage = prev + 1;
                        fetchNotices(nextPage);
                        return nextPage;
                    });
                }
            },
            { threshold: 0.1 } // 10%만 보여도 트리거
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, loading, fetchNotices]);

    const handleNoticeClick = (noticeCode: string) => {
        navigate(`/desk/notice/${noticeCode}`);
    };

    const isNewNotice = (regDate: string) => {
        const noticeDate = new Date(regDate);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - noticeDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7; // 7일 이내 공지사항은 NEW 표시
    };

    if (error && notices.length === 0) {
        return (
            <main className="container no-bottom-nav type_gray">
                <section className="bg-white">
                    <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                        {error}
                    </div>
                </section>
            </main>
        );
    }

    return (
        <>
            <main className="container no-bottom-nav type_gray">
                <section className="bg-white">
                    {notices.length === 0 && !loading ? (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            등록된 공지사항이 없습니다.
                        </div>
                    ) : (
                        <>
                            <ul className="board-list">
                                {notices.map((notice) => (
                                    <li key={notice.seq} className={isNewNotice(notice.regDate) ? 'new' : ''}>
                                        <a
                                            onClick={() => handleNoticeClick(notice.noticeCode)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <p className="txt">
                                                <span className="tit">
                                                    {notice.isPinned === 'Y' && '[공지] '}
                                                    {notice.title}
                                                </span>
                                                <span className="date">{formatDateTime(notice.regDate)}</span>
                                            </p>
                                            <span className="ico">
                                                <i className="fa fa-angle-right" aria-hidden="true"></i>
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            {/* Intersection Observer 타겟 */}
                            <div
                                ref={observerTarget}
                                style={{
                                    height: '20px',
                                    margin: '20px 0'
                                }}
                            />

                            {/* 로딩 인디케이터 */}
                            {loading && (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    <span>로딩 중...</span>
                                </div>
                            )}

                            {/* 더 이상 데이터가 없을 때 */}
                            {!hasMore && notices.length > 0 && (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#999',
                                    fontSize: '14px'
                                }}>
                                    모든 공지사항을 불러왔습니다.
                                </div>
                            )}
                        </>
                    )}
                </section>
            </main>
        </>
    );
};

export default NoticeListPage;